// 根据当前访问环境动态设置API基础地址
// 根据当前环境自动选择协议
const getBaseUrl = () => {
  // 统一走 HTTPS 域名：由 nginx 443(有 Let's Encrypt 证书) 反代到 gunicorn 127.0.0.1:8000。
  // 切勿直连 8000：那是 gunicorn 的纯 HTTP 端口，用 https:// 连会 TLS 握手失败，
  // 浏览器直接报 TypeError: Failed to fetch；且 47.121.116.163 这个 IP 没有 SSL 证书。
  return 'https://www.yangcongstudy.xin';
};

// 获取当前环境的API基础地址
const BASE_URL = getBaseUrl();

// 导出BASE_URL以便在其他组件中使用
export { BASE_URL };

// 协议处理函数 - 不再强制转换，保持原始协议
const enforceHttp = (url) => {
  // 不再强制转换HTTPS为HTTP，保持原始协议
  return url;
};

console.log('[API] 当前环境基础地址:', BASE_URL);

// 辅助函数：获取当前日期（不含时间），确保格式为YYYY-MM-DD
const getCurrentDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// 获取存储的令牌
const getAccessToken = () => localStorage.getItem('accessToken');
const getRefreshToken = () => localStorage.getItem('refreshToken');

// 刷新令牌
const refreshToken = async () => {
  const refresh = getRefreshToken();
  if (!refresh) {
    throw new Error('没有刷新令牌，需要重新登录');
  }

  try {
    const response = await fetch(`${BASE_URL}/api/token/refresh/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh }),
    });

    if (!response.ok) {
      throw new Error('刷新令牌失败');
    }

    const data = await response.json();
    localStorage.setItem('accessToken', data.access);
    return data.access;
  } catch (err) {
    // 刷新失败，清除令牌并抛出错误
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    throw err;
  }
};

// 带认证的fetch请求，添加重试机制
const fetchWithAuth = async (url, options = {}, retry = 2) => {
  // 确保URL以/开头并强制使用HTTP协议
  let fullUrl = url.startsWith('/') ? `${BASE_URL}${url}` : url;
  fullUrl = enforceHttp(fullUrl);
  console.log(`[API] 请求URL: ${fullUrl} (剩余重试次数: ${retry})`);

  // 设置默认headers
  const headers = { ...options.headers, 'Content-Type': 'application/json' };

  // 添加认证令牌
  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    console.log('[API] 已添加认证令牌');
  } else {
    console.log('[API] 无认证令牌');
  }

  // 合并选项
  const fetchOptions = { ...options, headers };

  try {
    // 发送请求
    console.log('[API] 开始发送请求');
    let response = await fetch(fullUrl, fetchOptions);
    console.log('[API] 请求响应状态:', response.status);

    // 处理401错误（令牌过期）
    if (response.status === 401 && token) {
      console.log('[API] 令牌过期，尝试刷新');
      try {
        // 尝试刷新令牌
        const newToken = await refreshToken();
        // 更新请求头中的令牌
        fetchOptions.headers['Authorization'] = `Bearer ${newToken}`;
        // 重新发送请求
        console.log('[API] 刷新令牌后重新发送请求');
        response = await fetch(fullUrl, fetchOptions);
        console.log('[API] 刷新后请求响应状态:', response.status);
      } catch (refreshError) {
        console.error('[API] 刷新令牌失败:', refreshError.message);
        // 刷新失败，保持原始响应
      }
    }

    return response;
  } catch (err) {
    console.error(`[API] 请求失败（剩余重试次数: ${retry}）:`, err.message);
    if (retry > 0) {
      // 重试请求
      console.log('[API] 开始重试请求');
      return fetchWithAuth(url, options, retry - 1);
    }
    throw err; // 重试耗尽后抛出错误
  }
};

// API方法
const api = {
  // 带认证的fetch请求
  fetchWithAuth,

  // 登录
  login: async (username, password) => {
    let loginUrl = `${BASE_URL}/api/token/`;
    loginUrl = enforceHttp(loginUrl);
    console.log('[API] 登录请求URL:', loginUrl);
    const response = await fetch(loginUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      throw new Error('登录失败');
    }

    return response.json();
  },

  // 获取用户信息
  getUserInfo: async () => {
    const response = await fetchWithAuth('/api/user/me/');

    if (!response.ok) {
      throw new Error('获取用户信息失败');
    }

    return response.json();
  },

  // 获取词汇列表（临时解决方案：只获取第一页，不处理分页）
  getVocabulary: async (queryString = '') => {
    const baseUrl = queryString ? `/api/sentences/vocabulary/?${queryString}` : '/api/sentences/vocabulary/';
    
    // 只请求第一页，不处理分页
    const response = await fetchWithAuth(baseUrl);
    
    if (!response.ok) {
      throw new Error('获取词汇列表失败');
    }
    
    const data = await response.json();
    
    // 直接返回第一页数据（无论是否有分页结构）
    return Array.isArray(data) ? data : (data.results || []);
  },

  // 上报测试结果
  reportTestResult: async (vocabId, errorCount) => {
    const currentDate = getCurrentDate();
    console.log('[API] 开始上报测试结果', { vocabId, errorCount, currentDate });

    try {
      const response = await fetchWithAuth('/api/sentences/test-history/', {
        method: 'POST',
        body: JSON.stringify({
          vocabulary: vocabId,
          test_date: currentDate,
          error_count: errorCount
        }),
      });

      console.log('[API] 上报测试结果响应状态:', response.status);

      if (!response.ok) {
        // 尝试获取错误详情
        try {
          const errorData = await response.json();
          console.error('[API] 上报测试结果失败详情:', errorData);
          throw new Error(`上报测试结果失败: 状态码 ${response.status}, 详情: ${JSON.stringify(errorData)}`);
        } catch (err) {
          throw new Error(`上报测试结果失败: 状态码 ${response.status}`);
        }
      }

      const data = await response.json();
      console.log('[API] 上报测试结果成功', data);
      return data;
    } catch (err) {
      console.error('[API] 上报测试结果异常:', err.message);
      throw err;
    }
  },

  // 获取测试历史
  getTestHistory: async () => {
    const response = await fetchWithAuth('/api/sentences/test-history/');

    if (!response.ok) {
      throw new Error('获取测试历史失败');
    }

    return response.json();
  },

  // 获取单词统计
  getVocabStats: async () => {
    const response = await fetchWithAuth('/api/sentences/vocabulary-stats/');

    if (!response.ok) {
      throw new Error('获取单词统计失败');
    }

    return response.json();
  },

  getWrongNotebookActive: async () => {
    const response = await fetchWithAuth('/api/sentences/wrong-notebook/?active=true');
    if (!response.ok) {
      throw new Error('获取错题本失败');
    }
    return response.json();
  },

  getWrongNotebookReviewCandidates: async () => {
    const response = await fetchWithAuth('/api/sentences/wrong-notebook/review_candidates/');
    if (!response.ok) {
      throw new Error('获取复习候选失败');
    }
    return response.json();
  },

  // 登出
  logout: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }
};

export default api;